import { useLayoutEffect, useRef, useState } from 'react';

export interface TileBox {
  /** Centre-to-centre distance to the next tile (tile width + gap). */
  step: number;
  /** Tile width in CSS px. */
  width: number;
  /** Tile height in CSS px — equals the reel scene height (tiles are h-full). */
  height: number;
}

/**
 * Measures the horizontal distance from one tile-centre to the next
 * (`width + gap`) at the breakpoint Tailwind currently resolves to.
 * Reads it straight off the live DOM so the slot animation tracks
 * container-query breakpoints (260 → 280 → 360 → 420 px) without
 * having to duplicate that table in JS. Re-measures on resize.
 *
 * Also mirrors the FULL tile box (step + width + height) in `boxRef` so the
 * consumer can freeze the whole geometry at spin-start without waiting on a
 * render. `tileStep` alone isn't enough: on iOS the scene height is `56vh`,
 * so the live tile size drifts when the toolbar collapses mid-spin and the
 * frozen step no longer matches the lane — the wheel lands off-centre.
 *
 * Attach the returned `ref` to one slot tile (any will do; we use
 * the lane's initial centre) and read `tileStep` / `boxRef` from there.
 */
export function useTileStep() {
  const ref = useRef<HTMLDivElement>(null);
  const [tileStep, setTileStep] = useState(0);
  const boxRef = useRef<TileBox>({ step: 0, width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const sibling = el.nextElementSibling as HTMLElement | null;
      const step = sibling
        ? sibling.getBoundingClientRect().left - rect.left
        : rect.width;
      boxRef.current = { step, width: rect.width, height: rect.height };
      setTileStep(step);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, tileStep, boxRef };
}
